import "./Home.css"
import React, { useEffect, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../redux/store";
import { logout } from "../../redux/actions/user.actions";
import {
  fetchSpasWithErrors,
  fetchSpasWithTempDifference,
  fetchSpasConnectedThisWeek,
  fetchSpasConnectedLastWeek,
  fetchSpasWithSbHours,
  fetchOnlineMovedSpas,
  fetchSpaLocations
} from "../../redux/actions/dashboard.actions";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import { 
  FlagIconFilled,
  FlagIconOutlined,
} from "../../assets/svg/svg";
import {
  Fade,
  Grow,
  Skeleton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import DashDetails from "./components/DashDetails";
import SpaErrorsCard from "./components/SpaErrorsCard";
import TempDiffCard from "./components/TempDiffCard";
import SpaManualCard from "./components/SpaManualCard";
import SpaConnectedCard from "./components/SpaConnectedCard";
import WeatherCard from "./components/WeatherCard";
import NavigationDrawer from "../../components/common/Header/NavigationDrawer";

// Lazy-loaded so maplibre-gl (~800KB) is code-split out of the initial dashboard bundle.
const SpaMapCard = React.lazy(() => import("./components/SpaMapCard"));

const HomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user.data);
  const isLoading = useSelector((state: RootState) => state.user.loading);

  const dashboard = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    // Spas with errors
    if (!dashboard.spasWithErrors.spa_guids && !dashboard.spasWithErrors.loading && !dashboard.spasWithErrors.error) {
      dispatch(fetchSpasWithErrors());
    }

    // Spas with temperature difference
    if (!dashboard.spasWithTempDifference.spa_guids && !dashboard.spasWithTempDifference.loading && !dashboard.spasWithTempDifference.error) {
      dispatch(fetchSpasWithTempDifference());
    }

    // Spas connected this week
    if (!dashboard.spasConnectedThisWeek.spa_guids && !dashboard.spasConnectedThisWeek.loading && !dashboard.spasConnectedThisWeek.error) {
      dispatch(fetchSpasConnectedThisWeek());
    }

    // Spas connected last week
    if (!dashboard.spasConnectedLastWeek.spa_guids && !dashboard.spasConnectedLastWeek.loading && !dashboard.spasConnectedLastWeek.error) {
      dispatch(fetchSpasConnectedLastWeek());
    }

    // Spas with SB hours
    if (!dashboard.spasWithSbHours.spa_guids && !dashboard.spasWithSbHours.loading && !dashboard.spasWithSbHours.error) {
      dispatch(fetchSpasWithSbHours());
    }

    // Online moved spas
    if (dashboard.onlineMovedSpas.spa_guids===null && !dashboard.onlineMovedSpas.loading && !dashboard.onlineMovedSpas.error) {
      dispatch(fetchOnlineMovedSpas());
    }

    // Approximate spa locations for the map. The map pins only spas that need
    // attention, so wait until those three sets have settled (loaded or errored) —
    // the thunk sends their union.
    const settled = (s: {spa_guids: any; loading: boolean; error: string | null}) =>
      !s.loading && (s.spa_guids !== null || s.error !== null);

    const attentionSetsReady =
      settled(dashboard.spasWithErrors) &&
      settled(dashboard.spasWithTempDifference) &&
      settled(dashboard.spasWithSbHours);

    if (attentionSetsReady && !dashboard.spaLocations.geojson && !dashboard.spaLocations.loading && !dashboard.spaLocations.error) {
      dispatch(fetchSpaLocations());
    }
  }, [
    dispatch,
    dashboard.spasWithErrors,
    dashboard.spasWithTempDifference,
    dashboard.spasConnectedThisWeek,
    dashboard.spasConnectedLastWeek,
    dashboard.spasWithSbHours,
    dashboard.onlineMovedSpas,
    dashboard.spaLocations,
  ]);

  /* The map is dropped on small screens rather than hidden in CSS: SpaMapCard is lazy-loaded,
     so not rendering it means phones never download the ~800KB maplibre chunk at all. Matches
     the breakpoints in Home.css that drop it from the grid. */
  const hideMap = useMediaQuery(
    "(max-width: 768px), (max-width: 1024px) and (orientation: landscape)",
  );

  const [openSpas, setOpenSpas] = useState(false);
  const [section, setSection] = useState("");
  const [guids, setGuids] = useState<any[]>([]);

  const handleOpen = (section:string, guids:any[]) => {
    setSection(section);
    setGuids(guids);
    setOpenSpas(!openSpas);
  }

  // Tabs in the spa list, so it can switch lists without going back to the dashboard
  const listSections = [
    {key: 'errors', title: 'Spas with Errors', guids: dashboard?.spasWithErrors?.spa_guids ?? [], count: dashboard?.spasWithErrors?.spa_guids?.length},
    {key: 'temp', title: 'Temperature Difference', guids: dashboard?.spasWithTempDifference?.spa_guids ?? [], count: dashboard?.spasWithTempDifference?.spa_guids?.length},
    {key: 'manual', title: 'Manual Mode', guids: dashboard?.spasWithSbHours?.spa_guids ?? [], count: dashboard?.spasWithSbHours?.spa_guids?.length},
    {key: 'flagged', title: 'Flagged', guids: [], count: user?.flagged_spas_count},
  ];

  const handleSectionChange = (key: string) => {
    const next = listSections.find((s) => s.key === key);
    if (!next) return;
    setSection(next.key);
    setGuids(next.guids);
  }
  
  return (
    <>
      <Header withBackButton={false} withUserAvatar={false}/>
    <div className="dash-container">
      {isLoading?
      <Skeleton variant="rectangular" width={'20%'} height={'100%'} sx={{zIndex:'999'}} animation='wave'/>
      :
      <nav className="nav-drawer">
        <NavigationDrawer/>
      </nav>}

      <div className="dash">

      {!openSpas && <Fade in={!openSpas}>
        <div className="dash-content">

          {/* Greeting band — who you are, and the flagged-spas shortcut. */}
          <div className="dash-header">
            <div className="dash-greeting">
              <h1>Welcome{user?.username ? `, ${user.username}` : ''}.</h1>
              <p className="dash-subtitle">Here's what needs your attention today.</p>
            </div>
            {user?.flagged_spas_count > 0 ?
              <Tooltip title={<p>You have <strong>{user?.flagged_spas_count}</strong> {user?.flagged_spas_count===1 ? `spa`:`spas`} flagged.</p>}>
                <div className={`flag-icon clickable-icon`} onClick={()=>handleOpen("flagged",[])}>
                  <FlagIconFilled/>
                </div>
              </Tooltip>
              :
              <Tooltip title={<p>No spas flagged.</p>}>
                <div className={`flag-icon disabled-icon`}>
                  <FlagIconOutlined/>
                </div>
              </Tooltip>
            }
          </div>

          {/* Needs-Attention KPI strip — the three actionable counts, each opens its detail list. */}
          <div className="kpi-strip">
            {dashboard?.spasWithErrors?.loading ?
              <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}}/>
              :
              <div className="spa-info kpi-tile clickable" id="spa-errors" onClick={()=>handleOpen("errors", dashboard?.spasWithErrors?.spa_guids??[])}>
                <SpaErrorsCard/>
              </div>
            }

            {dashboard?.spasWithTempDifference?.loading ?
              <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}}/>
              :
              <div className="spa-info kpi-tile clickable" id="spa-temp-diff" onClick={()=>handleOpen("temp", dashboard?.spasWithTempDifference?.spa_guids??[])}>
                <TempDiffCard/>
              </div>
            }

            {dashboard?.spasWithSbHours?.loading ?
              <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}}/>
              :
              <div className="spa-info kpi-tile clickable" id="spa-manual" onClick={()=>handleOpen("manual", dashboard?.spasWithSbHours?.spa_guids??[])}>
                <SpaManualCard/>
              </div>
            }
          </div>

          {/* Feature grid — the large map beside a stacked weather + connected column. */}
          <div className="feature-grid">
            {hideMap ? null : isLoading ?
              <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}} />
              :
              <div className="spa-info" id="map">
                <Suspense fallback={<Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}} />}>
                  <SpaMapCard/>
                </Suspense>
              </div>
            }

            <div className="feature-side">
              {isLoading ?
                <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}}/>
                :
                <div className="spa-info" id="weather">
                  <WeatherCard/>
                </div>
              }

              {dashboard?.spasConnectedLastWeek?.loading || dashboard?.spasConnectedThisWeek?.loading || dashboard?.onlineMovedSpas?.loading ?
                <Skeleton variant="rectangular" width={'100%'} height={'100%'} sx={{borderRadius:'25px', background:'var(--as-white)'}}/>
                :
                <div className="spa-info" id="spa-connected">
                  <SpaConnectedCard/>
                </div>
              }
            </div>
          </div>

        </div>
        </Fade>}

        {openSpas &&
          <Grow in={openSpas}>
            <div className="spa-info" id="dash-list">
              <DashDetails section={section} open={openSpas} setOpen={setOpenSpas} guids={guids} sections={listSections} onSectionChange={handleSectionChange}/>
            </div>
          </Grow>
        }
      </div>
    </div>  
    </>
  );
};

export default HomePage;
